import React, { ReactElement, useMemo, useRef, useEffect, useState, useCallback } from "react";
import { useQuery } from "react-query";
import { useToast } from "@chakra-ui/react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Form, FormElement } from "@progress/kendo-react-form";
import { Stepper } from "@progress/kendo-react-layout";
import { useLocalization } from "@progress/kendo-react-intl";
import { useWindowSize } from "usehooks-ts";
import { enMessages } from "../../../../messages/en-US";
import { ReactQueryKeys } from "../../../../Constants/ClientRoutingConstants";
import AssetTemplateDetailsComponent from "./AssetTemplateDetailsComponent";
import AssetTemplateConfigurationComponent from "./AssetTemplateConfigurationComponent";
import AssetTemplateGreasePumpComponent from "./AssetTemplateGreasePumpComponent";
import LoadingSpinner from "../../../Shared/Components/LoadingSpinner";
import templatesApiService from "../../Utilities/TemplatesApiService";
import useIdentityStore from "../../../../Zustand/identityStore";
import { buildTemplatePayload } from "./assetTemplateDialogUtils";
import { useAssetTemplateDialogState } from "./useAssetTemplateDialogState";
import TemplateSourceChoiceBlock from "./TemplateSourceChoiceBlock";
import AssetTemplateDialogFooter from "./AssetTemplateDialogFooter";
import AssetTemplateUpdateStepComponent from "./AssetTemplateUpdateStepComponent";

export interface AssetTemplateDialogProps {
  onClose: () => void;
  onSuccess?: () => void;
  assetTemplate?: any;
  existingTemplates?: any[];
  isLoadingTemplate?: boolean;
  showTemplateSourceChoice?: boolean;
  /** When true (Administration), templates are for all sites (siteId ""), account field hidden. */
  isAdministrationMode?: boolean;
}

export default function AssetTemplateDialog(props: AssetTemplateDialogProps): ReactElement {
  const {
    assetTemplate,
    onClose,
    onSuccess,
    existingTemplates,
    isLoadingTemplate = false,
    showTemplateSourceChoice = false,
    isAdministrationMode = false,
  } = props;

  const localizationService = useLocalization();
  const toast = useToast({ position: "bottom", duration: 5000, isClosable: true });
  const { width, height } = useWindowSize();
  const currentSiteId = useIdentityStore((s) => s.user?.profile?.current_site_id ?? "");
  const currentSiteName = useIdentityStore((s) => s.user?.profile?.current_site_name ?? "");

  const allTemplatesQuery = useQuery(
    [ReactQueryKeys.AllTemplatesQueryAll],
    () => templatesApiService.getTemplatesAssetsAll(),
    { enabled: showTemplateSourceChoice }
  );

  const existingTemplatesForCopy = useMemo(() => {
    if (!showTemplateSourceChoice) return existingTemplates;
    if (!allTemplatesQuery.data) return [];
    const all = allTemplatesQuery.data;
    return all.filter((t: { siteId?: string }) => {
      const sid = t.siteId ?? "";
      return sid === "" || sid === currentSiteId;
    });
  }, [showTemplateSourceChoice, allTemplatesQuery.data, currentSiteId, existingTemplates]);

  /** Templates for name uniqueness validation: Administration = siteId "", otherwise current site */
  const existingTemplatesForNameValidation = useMemo(() => {
    const base = existingTemplatesForCopy ?? existingTemplates ?? [];
    const targetSiteId = isAdministrationMode ? "" : currentSiteId;
    return base.filter((t: { siteId?: string }) => (t.siteId ?? "") === targetSiteId);
  }, [existingTemplatesForCopy, existingTemplates, currentSiteId, isAdministrationMode]);

  const state = useAssetTemplateDialogState({
    assetTemplate,
    showTemplateSourceChoice,
    isAdministrationMode,
    existingTemplates: existingTemplatesForCopy,
    currentSiteId,
    currentSiteName,
    isLoadingTemplate,
  });

  const {
    sourceOption,
    setSourceOption,
    baseTemplate,
    selectedExistingTemplateId,
    loadingCopyTemplate,
    handleExistingTemplateSelect,
    handleSelectNew,
    effectiveTemplate,
    showSourceChoiceInDetails,
    step,
    setStep,
    hasGreasePumps,
    groups,
    setGroups,
    endpoints,
    setEndpoints,
    gateways,
    setGateways,
    greasePumps,
    setGreasePumps,
    selectedGroupId,
    setSelectedGroupId,
    imageBase64,
    setImageBase64,
    isLoadingImage,
    isEdit,
    canProceedFromConfiguration,
    canSaveFromGreasePump,
    getInitialValues,
    handleGreasePumpChange,
    handlePrevious,
  } = state;

  const formRef = useRef<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkUpdateResult, setBulkUpdateResult] = useState<{
    totalAssetCount: number;
    updatedAssetCount: number;
    results: Array<{ name: string; updated: boolean }>;
  } | null>(null);
  const [pendingSaveFormData, setPendingSaveFormData] = useState<any>(null);

  // When a template is selected for copy, trigger name validation immediately
  // so duplicate name error shows without needing to blur the field
  useEffect(() => {
    if (!baseTemplate || loadingCopyTemplate) return;
    const timer = setTimeout(() => {
      formRef.current?.onBlur?.("name");
    }, 0);
    return () => clearTimeout(timer);
  }, [baseTemplate, loadingCopyTemplate]);

  const title = isEdit
    ? localizationService.toLanguageString("custom.edit", enMessages.custom.edit) + " Template"
    : localizationService.toLanguageString("custom.add", enMessages.custom.add) + " Template";

  const handleSubmit = async (formData: any) => {
    const templatePayload = buildTemplatePayload({
      formData,
      imageBase64,
      hasGreasePumps,
      groups,
      endpoints,
      gateways,
      greasePumps,
      useCurrentAccount: showTemplateSourceChoice,
      isAdministrationMode,
      currentSiteId,
      currentSiteName,
    });

    setIsSubmitting(true);
    setBulkUpdateResult(null);
    try {
      if (isEdit && assetTemplate?.id) {
        await templatesApiService.updateTemplateAsset(assetTemplate.id, templatePayload as any);
        // Bulk update is triggered by AssetTemplateUpdateStepComponent when step is shown (non-Administration only).
        // In Administration mode, no bulk update; close and refresh.
        if (isAdministrationMode) {
          onSuccess?.();
          onClose();
        }
      } else {
        await templatesApiService.createTemplateAsset(templatePayload as any);
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      console.error("Error saving template:", err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDialogWidth = () => {
    const onUpdateStep = isEdit && step === (hasGreasePumps ? 3 : 2);
    if (step === 0 || onUpdateStep) return "min(max(90vw, 65vw), 900px)";
    return "90vw";
  };

  const buildFormDataFromRenderProps = (formRenderProps: any) => ({
    name: formRenderProps.valueGetter("name"),
    type: formRenderProps.valueGetter("type"),
    account: formRenderProps.valueGetter("account"),
    image: formRenderProps.valueGetter("image"),
    hasGreasePumps: formRenderProps.valueGetter("hasGreasePumps"),
  });

  const isNextLeadingToUpdateAssets =
    isEdit &&
    !isAdministrationMode &&
    ((step === 1 && !hasGreasePumps) || (step === 2 && hasGreasePumps));

  const handleNext = (formRenderProps: any) => {
    setBulkUpdateResult(null);
    if (isNextLeadingToUpdateAssets) {
      setPendingSaveFormData(buildFormDataFromRenderProps(formRenderProps));
    } else {
      setPendingSaveFormData(null);
    }
    setStep(step + 1);
  };

  const saveTemplateFirst = useCallback(() => {
    if (!pendingSaveFormData) return Promise.resolve();
    const templatePayload = buildTemplatePayload({
      formData: pendingSaveFormData,
      imageBase64,
      hasGreasePumps,
      groups,
      endpoints,
      gateways,
      greasePumps,
      useCurrentAccount: showTemplateSourceChoice,
      isAdministrationMode,
      currentSiteId,
      currentSiteName,
    });
    return templatesApiService.updateTemplateAsset(assetTemplate!.id, templatePayload as any);
  }, [
    pendingSaveFormData,
    imageBase64,
    hasGreasePumps,
    groups,
    endpoints,
    gateways,
    greasePumps,
    showTemplateSourceChoice,
    isAdministrationMode,
    currentSiteId,
    currentSiteName,
    assetTemplate,
  ]);

  return (
    <Dialog
      title={title}
      onClose={onClose}
      width={getDialogWidth()}
      height={height - 50}
    >
      <div className={`h-full flex flex-col overscroll-contain ${width < 768 ? "overflow-y-auto" : ""}`}>
        {isLoadingTemplate ||
        (showTemplateSourceChoice && !assetTemplate && allTemplatesQuery.isLoading) ? (
          <div className="flex flex-grow items-center justify-center min-h-[200px]">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex-grow flex flex-col p-4">
            <div className="mb-4">
              <Stepper
                value={step}
                items={(() => {
                  const base = [
                    {
                      label: localizationService.toLanguageString("custom.details", enMessages.custom.details),
                      isValid: true,
                    },
                    {
                      label: localizationService.toLanguageString("custom.configuration", enMessages.custom.configuration),
                      isValid: true,
                    },
                  ];
                  if (hasGreasePumps) {
                    base.push({
                      label: localizationService.toLanguageString("custom.greasePump", enMessages.custom.greasePump),
                      isValid: true,
                    });
                  }
                  if (isEdit && !isAdministrationMode) {
                    base.push({
                      label: localizationService.toLanguageString("custom.updateAssets", enMessages.custom.updateAssets),
                      isValid: true,
                    });
                  }
                  return base;
                })()}
              />
            </div>

            <Form
              ref={formRef}
              key={baseTemplate ? `copy-${baseTemplate._sourceId ?? selectedExistingTemplateId}` : assetTemplate?.id ?? "new"}
              onSubmit={handleSubmit}
              initialValues={getInitialValues()}
              render={(formRenderProps) => (
                <FormElement className="flex flex-col flex-grow">
                  <div className="flex-grow">
                    {step === 0 && (
                      <div className="space-y-4">
                        {showSourceChoiceInDetails && (
                          <TemplateSourceChoiceBlock
                            sourceOption={sourceOption}
                            onSelectNew={handleSelectNew}
                            onSelectExisting={() => setSourceOption("existing")}
                            selectedExistingTemplateId={selectedExistingTemplateId}
                            existingTemplates={existingTemplatesForCopy}
                            loadingCopyTemplate={loadingCopyTemplate || (showTemplateSourceChoice && allTemplatesQuery.isLoading)}
                            onExistingTemplateSelect={handleExistingTemplateSelect}
                          />
                        )}
                        {(!showSourceChoiceInDetails || sourceOption === "new" || baseTemplate) && (
                          <AssetTemplateDetailsComponent
                            assetTemplate={effectiveTemplate}
                            formRenderProps={formRenderProps}
                            existingTemplates={existingTemplatesForNameValidation}
                            onGreasePumpChange={handleGreasePumpChange}
                            hideAccountField={showTemplateSourceChoice || isAdministrationMode}
                            templateImage={effectiveTemplate ? imageBase64 : null}
                            isLoadingTemplateImage={!!(effectiveTemplate && isLoadingImage)}
                          />
                        )}
                      </div>
                    )}

                    {step === 1 && (
                      <AssetTemplateConfigurationComponent
                        formRenderProps={formRenderProps}
                        groups={groups}
                        setGroups={setGroups}
                        endpoints={endpoints}
                        setEndpoints={setEndpoints}
                        gateways={gateways}
                        setGateways={setGateways}
                        greasePumps={greasePumps}
                        setGreasePumps={setGreasePumps}
                        selectedGroupId={selectedGroupId}
                        setSelectedGroupId={setSelectedGroupId}
                        isEditMode={isEdit}
                        imageBase64={imageBase64}
                        setImageBase64={setImageBase64}
                      />
                    )}

                    {step === 2 && hasGreasePumps && (
                      <AssetTemplateGreasePumpComponent
                        formRenderProps={formRenderProps}
                        groups={groups}
                        endpoints={endpoints}
                        greasePumps={greasePumps}
                        setGreasePumps={setGreasePumps}
                        imageBase64={imageBase64}
                        isEditMode={isEdit}
                      />
                    )}
                    {step === (hasGreasePumps ? 3 : 2) && isEdit && !isAdministrationMode && (
                      <AssetTemplateUpdateStepComponent
                        templateId={assetTemplate?.id ?? ""}
                        saveTemplateFirst={pendingSaveFormData ? saveTemplateFirst : undefined}
                        onBulkUpdateComplete={(result) => {
                          setBulkUpdateResult(result);
                          setIsBulkUpdating(false);
                        }}
                        onBulkUpdateStart={() => setIsBulkUpdating(true)}
                      />
                    )}
                  </div>

                  <AssetTemplateDialogFooter
                    step={step}
                    hasGreasePumps={hasGreasePumps}
                    formValid={formRenderProps.valid}
                    isSubmitting={isSubmitting}
                    isBulkUpdating={isBulkUpdating}
                    bulkUpdateResult={bulkUpdateResult}
                    canProceedFromConfiguration={canProceedFromConfiguration}
                    canSaveFromGreasePump={canSaveFromGreasePump}
                    showSourceChoiceInDetails={showSourceChoiceInDetails}
                    sourceOption={sourceOption}
                    baseTemplate={baseTemplate}
                    loadingCopyTemplate={loadingCopyTemplate}
                    isEdit={isEdit}
                    isAdministrationMode={isAdministrationMode}
                    isLoadingImage={isLoadingImage}
                    onClose={onClose}
                    onCloseAfterBulkUpdate={onSuccess}
                    onPrevious={() => {
                      if (isEdit && step === (hasGreasePumps ? 3 : 2)) {
                        setBulkUpdateResult(null);
                        setIsBulkUpdating(false);
                        setPendingSaveFormData(null);
                      }
                      handlePrevious();
                    }}
                    onNext={() => handleNext(formRenderProps)}
                    onSave={() => handleSubmit(buildFormDataFromRenderProps(formRenderProps))}
                  />
                </FormElement>
              )}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
